import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface SyncReportData {
  source_id: string;
  remote_commit?: string;
  added?: string[];
  updated?: string[];
  removed?: string[];
  skipped?: Array<{ path?: string; name?: string; reason?: string }>;
  total_skills?: number;
  total_before?: number;
  total_after?: number;
  error?: string;
  status?: string;
}

interface SyncReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: SyncReportData | SyncReportData[] | null;
}

const ChangeList: React.FC<{ items: string[]; color: string; label: string; darkMode: boolean }> = ({
  items, color, label, darkMode,
}) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${color}`}>{label} ({items.length})</p>
      <ul className={`text-xs space-y-0.5 pl-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {items.map((name) => <li key={name}>- {name}</li>)}
      </ul>
    </div>
  );
};

const SkippedSection: React.FC<{
  skipped: Array<{ path?: string; name?: string; reason?: string }>;
  darkMode: boolean;
}> = ({ skipped, darkMode }) => {
  const [expanded, setExpanded] = useState(false);

  // Group by reason for a compact summary
  const reasonCounts: Record<string, number> = {};
  for (const s of skipped) {
    const reason = s.reason ?? 'Unknown';
    reasonCounts[reason] = (reasonCounts[reason] ?? 0) + 1;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-xs font-semibold uppercase tracking-wide text-amber-400 hover:text-amber-300 flex items-center gap-1"
      >
        <span className="inline-block transition-transform" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          ▸
        </span>
        Skipped ({skipped.length})
      </button>
      {expanded && (
        <div className="mt-1 space-y-1">
          {Object.entries(reasonCounts).map(([reason, count]) => (
            <p key={reason} className={`text-xs pl-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {count}x — {reason}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const SingleReport: React.FC<{ report: SyncReportData; darkMode: boolean }> = ({ report, darkMode }) => {
  if (report.error) {
    return (
      <div className={`rounded-md p-3 text-sm ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
        <p className="font-medium">{report.source_id}</p>
        <p className="text-xs mt-1">{report.error}</p>
      </div>
    );
  }
  if (report.status === 'skipped_disabled') {
    return (
      <div className={`rounded-md p-3 text-sm ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
        <p>{report.source_id} — skipped (disabled)</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex gap-4 text-sm">
        <span className="text-green-500">+{report.added?.length ?? 0} added</span>
        <span className="text-blue-400">~{report.updated?.length ?? 0} updated</span>
        <span className="text-red-400">-{report.removed?.length ?? 0} removed</span>
        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{report.skipped?.length ?? 0} skipped</span>
      </div>
      {report.remote_commit && (
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Commit: {report.remote_commit.slice(0, 8)} | Total: {report.total_skills ?? '?'} skills
        </p>
      )}
      <ChangeList items={report.added ?? []} color="text-green-500" label="Added" darkMode={darkMode} />
      <ChangeList items={report.updated ?? []} color="text-blue-400" label="Updated" darkMode={darkMode} />
      <ChangeList items={report.removed ?? []} color="text-red-400" label="Removed" darkMode={darkMode} />
      {(report.skipped ?? []).length > 0 && (
        <SkippedSection skipped={report.skipped!} darkMode={darkMode} />
      )}
    </div>
  );
};

function reportToMarkdown(reports: SyncReportData[]): string {
  const lines: string[] = [`# Sync Report — ${reports.length} source${reports.length !== 1 ? 's' : ''}`, ''];
  for (const r of reports) {
    lines.push(`## ${r.source_id}`);
    if (r.error) { lines.push(`**Error:** ${r.error}`, ''); continue; }
    if (r.status === 'skipped_disabled') { lines.push('_Skipped (disabled)_', ''); continue; }
    lines.push(`| Metric | Count |`);
    lines.push(`|--------|-------|`);
    lines.push(`| Added | ${r.added?.length ?? 0} |`);
    lines.push(`| Updated | ${r.updated?.length ?? 0} |`);
    lines.push(`| Removed | ${r.removed?.length ?? 0} |`);
    lines.push(`| Skipped | ${r.skipped?.length ?? 0} |`);
    lines.push('');
    if (r.remote_commit) lines.push(`Commit: \`${r.remote_commit.slice(0, 8)}\` | Total: ${r.total_skills ?? '?'} skills`, '');
    for (const [label, items] of [['Added', r.added], ['Updated', r.updated], ['Removed', r.removed]] as const) {
      if (items && items.length > 0) {
        lines.push(`### ${label} (${items.length})`, ...items.map(n => `- ${n}`), '');
      }
    }
    if (r.skipped && r.skipped.length > 0) {
      lines.push(`### Skipped (${r.skipped.length})`, ...r.skipped.map(s => `- ${s.path ?? s.name ?? 'unknown'}: ${s.reason ?? 'no reason'}`), '');
    }
  }
  return lines.join('\n');
}

export const SyncReportModal: React.FC<SyncReportModalProps> = ({ isOpen, onClose, report }) => {
  const { darkMode } = useTheme();
  const [copied, setCopied] = useState(false);
  if (!isOpen || !report) return null;

  const reports = Array.isArray(report) ? report : [report];
  const title = reports.length === 1 ? `Sync Report — ${reports[0].source_id}` : `Sync Report — ${reports.length} sources`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(reportToMarkdown(reports));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`w-full max-w-lg mx-4 rounded-lg shadow-2xl border p-6 max-h-[80vh] flex flex-col ${
        darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-4">
          {reports.map((r, i) => (
            <div key={r.source_id || i}>
              {reports.length > 1 && (
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {r.source_id}
                </h4>
              )}
              <SingleReport report={r} darkMode={darkMode} />
              {i < reports.length - 1 && <hr className={`my-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} />}
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleCopy}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
              copied
                ? darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-700'
                : darkMode ? 'bg-indigo-700 hover:bg-indigo-600 text-white' : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            {copied ? 'Copied!' : 'Copy as Markdown'}
          </button>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
